export function normalisePartReference(value) {
  return String(value ?? '').trim().toUpperCase().replace(/\s+/g, '');
}

export function expandReferenceParts(manifest, parts) {
  if (!Array.isArray(manifest.parts) || parts.length !== manifest.parts.length) throw new Error('Incomplete catalogue');
  const products = parts.flatMap((part, index) => {
    if (!Array.isArray(part.products) || part.products.length !== manifest.parts[index].products) throw new Error('Incomplete catalogue part');
    return part.products;
  });
  if (products.length !== manifest.productCount || products.reduce((sum, product) => sum + (product.variants?.length ?? 0), 0) !== manifest.variantCount) throw new Error('Incomplete catalogue counts');
  return { ...manifest, products };
}

export function buildReferenceIndex(data) {
  if (!Array.isArray(data.products)) throw new Error('Invalid reference catalogue');
  return data.products.map((product) => {
    const references = new Set();
    const values = [product.sku, product.partNumber, ...(product.crossReferences ?? []), ...(product.barcodes ?? []), ...(product.aliases ?? [])];
    for (const variant of product.variants ?? []) values.push(variant.sku, variant.barcode);
    for (const detail of product.referenceDetails ?? []) values.push(detail.code);
    for (const value of values) {
      const key = normalisePartReference(value);
      if (key) references.add(key);
    }
    return { product, references, titleText: String(product.title ?? '').toLocaleLowerCase('en-GB') };
  });
}

export function findReferenceProducts(index, { query = '', family = '' } = {}) {
  const scoped = index.filter(({ product }) => !family || product.family === family);
  const key = normalisePartReference(query);
  if (!key) return { type: 'catalogue_results', products: scoped.map(({ product }) => product) };
  const exact = scoped.filter(({ references }) => references.has(key));
  if (exact.length) return { type: 'exact', products: exact.map(({ product }) => product) };
  const words = String(query).trim().toLocaleLowerCase('en-GB').split(/\s+/).filter(Boolean);
  return { type: 'title_matches', products: scoped.filter(({ titleText }) => words.every((word) => titleText.includes(word))).map(({ product }) => product) };
}

export function retailPriceSummary(product) {
  const variants = product.variants ?? [];
  const amounts = variants.map((variant) => variant.retailPrice).filter((value) => value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0).map(Number);
  if (!amounts.length) return null;
  return { min: Math.min(...amounts), max: Math.max(...amounts), currency: product.currency || variants.find((variant) => variant.currency)?.currency || 'GBP', partial: amounts.length < variants.length };
}

export function ukVatComparison(value) {
  if (value == null || value === '' || !Number.isFinite(Number(value)) || Number(value) < 0) return null;
  const netPence = Math.round(Number(value) * 100);
  return { net: netPence / 100, gross: Math.round(netPence * 120 / 100) / 100 };
}

export function findVehicleTextMatches(applications, query) {
  const words = String(query ?? '').trim().toLocaleLowerCase('en-GB').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return applications.filter((row) => {
    const text = `${row.make} ${row.model} ${row.yearLabel}`.toLocaleLowerCase('en-GB');
    return words.every((word) => text.includes(word));
  });
}

export function referenceImagesForProduct(imageData, product) {
  if (imageData?.schemaVersion !== 1 || !product?.handle || !Object.hasOwn(imageData.byHandle ?? {}, product.handle)) return [];
  const variantSkus = new Set((product.variants ?? []).map((variant) => variant.sku));
  const seen = new Set();
  return (Array.isArray(imageData.byHandle[product.handle]) ? imageData.byHandle[product.handle] : []).filter((image) => {
    try {
      const url = new URL(image.url);
      if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || !url.pathname.startsWith('/s/files/') || url.username || url.password || seen.has(url.href)) return false;
      if (image.variantSkus != null && (!Array.isArray(image.variantSkus) || image.variantSkus.some((sku) => !variantSkus.has(sku)))) return false;
      seen.add(url.href);
      return true;
    } catch { return false; }
  });
}

export function referenceImageUrl(source, width) {
  const url = new URL(source);
  url.searchParams.set('width', String(width));
  return url.href;
}

export function ajaxReferenceImages(data, product, fallback = []) {
  if (!data || data.handle !== product?.handle) return null;
  const expectedSkus = (product.variants ?? []).map((variant) => variant.sku).filter(Boolean);
  if (expectedSkus.length && (!Array.isArray(data.variants) || !data.variants.some((variant) => expectedSkus.includes(variant.sku)))) return null;
  const media = Array.isArray(data.media) ? data.media.filter((item) => item?.media_type === 'image') : (Array.isArray(data.images) ? data.images : []).map((src) => ({ src }));
  const images = media.slice(0, 250).flatMap((item) => {
    try {
      const url = new URL(item.src || item.preview_image?.src, 'https://cdn.shopify.com').href;
      const previous = fallback.find((image) => image.url === url);
      return [{ url, alt: item.alt || previous?.alt || product.title || product.sku, width: item.width || item.preview_image?.width, height: item.height || item.preview_image?.height, ...(previous?.variantSkus ? { variantSkus: previous.variantSkus } : {}) }];
    } catch { return []; }
  });
  return referenceImagesForProduct({ schemaVersion: 1, byHandle: { [product.handle]: images } }, product);
}

const ElementBase = globalThis.HTMLElement ?? class {};

class RemaxReferenceFinder extends ElementBase {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    this.translations = JSON.parse(this.querySelector('[data-translations]').textContent);
    this.form = this.querySelector('form');
    this.controls = this.querySelector('[data-controls]');
    this.query = this.querySelector('[data-query]');
    this.family = this.querySelector('[data-family]');
    this.status = this.querySelector('[data-status]');
    this.resultHeading = this.querySelector('[data-result-heading]');
    this.results = this.querySelector('[data-results]');
    this.more = this.querySelector('[data-more]');
    this.retry = this.querySelector('[data-retry]');
    this.vehicleArea = this.querySelector('[data-vehicle-area]');
    this.vehicleStatus = this.querySelector('[data-vehicle-status]');
    this.vehicleResults = this.querySelector('[data-vehicle-results]');
    this.searchVersion = 0;
    this.pageSize = 12;
    this.productImageRequests = new Map();
    this.productImageQueue = [];
    this.activeImageRequests = 0;
    this.imageObserver = globalThis.IntersectionObserver ? new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.imageObserver.unobserve(entry.target);
        this.queueProductImages(entry.target);
      }
    }, { rootMargin: '350px 0px' }) : null;
    this.form.addEventListener('submit', (event) => { event.preventDefault(); this.renderResults(); });
    this.family.addEventListener('change', () => this.renderResults());
    this.more.addEventListener('click', () => this.appendProducts());
    this.retry.addEventListener('click', () => this.load());
    this.querySelector('[data-clear]').addEventListener('click', () => { this.query.value = ''; this.family.value = ''; this.renderResults(); });
    this.load();
  }

  t(key, values = {}) {
    let text = this.translations[key] ?? key;
    for (const [name, value] of Object.entries(values)) text = text.replaceAll(`[[${name}]]`, String(value));
    return text;
  }

  el(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = String(text);
    return element;
  }

  async load() {
    this.controls.disabled = true;
    this.retry.hidden = true;
    this.status.textContent = this.t('loading');
    this.loadImages();
    try {
      const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Catalogue unavailable');
      let data = await response.json();
      if (data.parts) {
        const sources = JSON.parse(this.querySelector('[data-catalogue-parts]').textContent);
        const parts = await Promise.all(data.parts.map(async (part) => {
          if (!sources[part.filename]) throw new Error('Missing catalogue source');
          const response = await fetch(sources[part.filename], { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalogue part unavailable');
          return response.json();
        }));
        data = expandReferenceParts(data, parts);
      }
      if (data.priceBasis !== 'ex_vat' || data.currency !== 'GBP') throw new Error('Unconfirmed price basis');
      this.index = buildReferenceIndex(data);
      const params = new URLSearchParams(window.location.search);
      this.query.value = params.get('q') ?? params.get('part') ?? '';
      this.family.value = params.get('family') ?? '';
      this.controls.disabled = false;
      this.renderResults();
    } catch {
      this.status.textContent = this.t('error');
      this.retry.hidden = false;
    }
  }

  async loadImages() {
    if (!this.dataset.imageSource) return;
    try {
      const response = await fetch(this.dataset.imageSource, { credentials: 'same-origin' });
      if (!response.ok) return;
      const images = await response.json();
      if (images.schemaVersion !== 1 || !images.byHandle || typeof images.byHandle !== 'object') return;
      this.imageData = images;
      for (const article of this.results.children) {
        const context = article.remaxImageContext;
        if (!context || context.currentLoaded) continue;
        const gallery = this.renderImages(context.product);
        if (gallery) context.slot.replaceChildren(gallery);
      }
    } catch {}
  }

  queueProductImages(article) {
    if (article.remaxImageContext?.queued) return;
    article.remaxImageContext.queued = true;
    this.productImageQueue.push(article);
    this.runImageQueue();
  }

  runImageQueue() {
    while (this.activeImageRequests < 4 && this.productImageQueue.length) {
      const article = this.productImageQueue.shift();
      if (!article.isConnected || article.remaxImageContext.version !== this.searchVersion) continue;
      this.activeImageRequests += 1;
      this.syncProductImages(article).finally(() => {
        this.activeImageRequests -= 1;
        this.runImageQueue();
      });
    }
  }

  async productImages(product) {
    if (!product?.handle) return null;
    if (!this.productImageRequests.has(product.handle)) {
      const request = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
          if (root.origin !== window.location.origin || !root.pathname.endsWith('/')) return null;
          const url = new URL(`products/${encodeURIComponent(product.handle)}.js`, root);
          const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) return null;
          const data = await response.json();
          return ajaxReferenceImages(data, product, referenceImagesForProduct(this.imageData, product));
        } catch { return null; }
        finally { clearTimeout(timeout); }
      })();
      this.productImageRequests.set(product.handle, request);
    }
    return this.productImageRequests.get(product.handle);
  }

  async syncProductImages(article) {
    const context = article.remaxImageContext;
    const images = await this.productImages(context.product);
    if (images === null || !article.isConnected || context.version !== this.searchVersion) return;
    context.currentLoaded = true;
    const existing = [...context.slot.querySelectorAll('.rm-reference-thumbnail img')].map((image) => image.src);
    const mainLink = context.slot.querySelector('.rm-reference-main-image');
    const previousUrls = existing.length ? existing : (mainLink ? [mainLink.href] : []);
    const unchanged = previousUrls.length === images.length && images.every((image, index) => {
      const previous = new URL(previousUrls[index]);
      previous.searchParams.delete('width');
      return previous.href === image.url;
    });
    if (unchanged) return;
    const gallery = this.renderImages(context.product, images);
    context.slot.replaceChildren(...(gallery ? [gallery] : []));
  }

  renderImages(product, images = referenceImagesForProduct(this.imageData, product)) {
    if (!images.length) return null;
    const gallery = this.el('figure', 'rm-reference-images');
    const fullSize = this.el('a', 'rm-reference-main-image');
    const main = this.el('img');
    main.loading = 'lazy';
    main.decoding = 'async';
    main.sizes = '(max-width: 649px) calc(100vw - 84px), (max-width: 999px) calc(50vw - 72px), 480px';
    const caption = this.el('figcaption', 'rm-reference-image-caption');
    const thumbnails = this.el('div', 'rm-reference-thumbnails');
    const buttons = [];
    const selectImage = (image, index) => {
      main.alt = image.alt || product.title || product.sku;
      main.width = Number(image.width) > 0 ? Number(image.width) : 600;
      main.height = Number(image.height) > 0 ? Number(image.height) : 600;
      main.srcset = [240, 480, 720, 960].map((width) => `${referenceImageUrl(image.url, width)} ${width}w`).join(', ');
      main.src = referenceImageUrl(image.url, 720);
      fullSize.href = image.url;
      caption.textContent = (image.variantSkus ?? []).join(', ');
      caption.hidden = !caption.textContent;
      buttons.forEach((button, buttonIndex) => button.setAttribute('aria-pressed', String(buttonIndex === index)));
    };
    main.addEventListener('error', () => { gallery.hidden = true; });
    fullSize.append(main);
    gallery.append(fullSize);
    if (images.length > 1) {
      images.forEach((image, index) => {
        const button = this.el('button', 'rm-reference-thumbnail');
        button.type = 'button';
        button.setAttribute('aria-label', image.alt || product.title || product.sku);
        const thumbnail = this.el('img');
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        thumbnail.width = 80;
        thumbnail.height = 80;
        thumbnail.src = referenceImageUrl(image.url, 160);
        thumbnail.addEventListener('error', () => { button.hidden = true; });
        button.append(thumbnail);
        button.addEventListener('click', () => selectImage(image, index));
        buttons.push(button);
        thumbnails.append(button);
      });
      gallery.append(thumbnails);
    }
    gallery.append(caption);
    selectImage(images[0], 0);
    return gallery;
  }

  async loadApplications() {
    if (!this.applicationPromise) {
      this.applicationPromise = Promise.all([
        import(this.dataset.applicationModule),
        fetch(this.dataset.applicationSource, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error('Applications unavailable');
          return response.json();
        }),
      ]).then(([module, data]) => {
        const applications = module.expandLookupData(data);
        return { module, applications, index: module.buildApplicationIndex(applications) };
      }).catch((error) => { this.applicationPromise = null; throw error; });
    }
    return this.applicationPromise;
  }

  renderResults() {
    if (!this.index) return;
    const version = ++this.searchVersion;
    this.imageObserver?.disconnect();
    this.productImageQueue.length = 0;
    this.results.replaceChildren();
    this.vehicleResults.replaceChildren();
    this.more.hidden = true;
    this.shown = 0;
    const params = new URLSearchParams(window.location.search);
    for (const key of ['q', 'part', 'family']) params.delete(key);
    if (this.query.value.trim()) params.set('q', this.query.value.trim());
    if (this.family.value) params.set('family', this.family.value);
    const suffix = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${suffix ? `?${suffix}` : ''}${window.location.hash}`);
    const result = findReferenceProducts(this.index, { query: this.query.value, family: this.family.value });
    this.filtered = result.products;
    this.resultHeading.textContent = this.t(result.type);
    if (this.filtered.length) this.appendProducts();
    else this.status.textContent = this.t('no_results');
    this.vehicleArea.hidden = !this.query.value.trim();
    if (!this.vehicleArea.hidden) this.renderVehicles(this.query.value, version);
  }

  appendProducts() {
    const fragment = document.createDocumentFragment();
    const next = this.filtered.slice(this.shown, this.shown + this.pageSize);
    const articles = next.map((product) => this.renderProduct(product));
    for (const article of articles) fragment.append(article);
    this.results.append(fragment);
    for (const article of articles) {
      if (this.imageObserver) this.imageObserver.observe(article);
      else this.queueProductImages(article);
    }
    this.shown += next.length;
    this.status.textContent = this.t('count', { shown: this.shown.toLocaleString('en-GB'), total: this.filtered.length.toLocaleString('en-GB') });
    this.more.hidden = this.shown >= this.filtered.length;
  }

  detailRow(dl, label, value) {
    if (value == null || value === '') return;
    const row = this.el('div');
    row.append(this.el('dt', '', label), this.el('dd', '', value));
    dl.append(row);
  }

  renderProduct(product) {
    const article = this.el('article', 'rm-reference-card');
    const head = this.el('div', 'rm-reference-card-heading');
    head.append(this.el('p', 'rm-kicker', product.displayPartNumber || product.partNumber || product.sku));
    const heading = this.el('h2');
    const canOpenProduct = this.dataset.productLinks === 'true' && product.handle;
    if (canOpenProduct) {
      const link = this.el('a', '', product.title);
      link.href = `/products/${encodeURIComponent(product.handle)}`;
      heading.append(link);
    } else {
      const link = this.el('a', '', product.title);
      link.href = `/pages/reference-lookup?q=${encodeURIComponent(product.sku || product.partNumber)}`;
      heading.append(link);
    }
    head.append(heading);
    article.append(head);
    const imageSlot = this.el('div', 'rm-reference-image-slot');
    const images = this.renderImages(product);
    if (images) imageSlot.append(images);
    article.append(imageSlot);
    article.remaxImageContext = { product, slot: imageSlot, version: this.searchVersion };
    const basics = this.el('dl', 'rm-reference-basics');
    this.detailRow(basics, this.t('code'), product.displayPartNumber || product.partNumber || product.sku);
    const barcodes = [...new Set([...(product.barcodes ?? []), ...(product.variants ?? []).map((variant) => variant.barcode)].filter(Boolean))];
    this.detailRow(basics, this.t('ean'), barcodes.join(', '));
    this.detailRow(basics, this.t('pack'), product.packSizeText || product.numericPackSize);
    article.append(basics);
    const retail = this.el('div', 'rm-reference-retail');
    retail.append(this.el('span', 'rm-retail-label', this.t('retail_price')));
    const retailSummary = retailPriceSummary(product);
    if (retailSummary) {
      const format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: retailSummary.currency });
      const price = retailSummary.min === retailSummary.max ? format.format(retailSummary.min) : `${format.format(retailSummary.min)} – ${format.format(retailSummary.max)}`;
      retail.append(this.el('strong', 'rm-retail-price', `${price} ${retailSummary.currency}`));
      const grossMin = ukVatComparison(retailSummary.min).gross;
      const grossMax = ukVatComparison(retailSummary.max).gross;
      const gross = grossMin === grossMax ? format.format(grossMin) : `${format.format(grossMin)} – ${format.format(grossMax)}`;
      retail.append(this.el('span', 'rm-retail-gross', `${gross} ${retailSummary.currency} ${this.t('uk_vat_inclusive')}`));
      retail.append(this.el('p', 'rm-retail-note', this.t('overseas_tax_note')));
      if (retailSummary.partial) retail.append(this.el('p', 'rm-retail-note', this.t('retail_partial')));
    } else retail.append(this.el('strong', 'rm-retail-note', this.t('retail_pending')));
    article.append(retail);
    if (product.bodyPlainSummary && product.bodyPlainSummary !== product.title) article.append(this.el('p', 'rm-reference-description', product.bodyPlainSummary));
    const details = this.el('details', 'rm-reference-detail');
    details.append(this.el('summary', '', this.t('details')));
    const references = this.el('div', 'rm-reference-detail-group');
    references.append(this.el('h3', '', this.t('codes')));
    for (const clause of product.originalReferenceClauses ?? []) references.append(this.el('p', 'rm-reference-original-clause', clause));
    if (product.referenceDetails?.length) {
      const list = this.el('ul', 'rm-reference-codes');
      const seen = new Set();
      for (const item of product.referenceDetails) {
        const text = [item.brand, item.code].filter(Boolean).join(' ');
        if (!seen.has(text)) list.append(this.el('li', '', text));
        seen.add(text);
      }
      references.append(list);
    } else if (product.crossReferences?.length) {
      const list = this.el('ul', 'rm-reference-codes');
      for (const reference of product.crossReferences) list.append(this.el('li', '', reference));
      references.append(list);
    } else references.append(this.el('p', 'rm-reference-muted', this.t('no_codes')));
    details.append(references);
    const specs = this.el('div', 'rm-reference-detail-group');
    specs.append(this.el('h3', '', this.t('specs')));
    if (product.specifications?.length) {
      const table = this.el('table', 'rm-reference-spec-table');
      const tbody = this.el('tbody');
      for (const specification of product.specifications) {
        const row = this.el('tr');
        const th = this.el('th', '', specification.label || specification.key);
        th.scope = 'row';
        row.append(th, this.el('td', '', specification.value));
        tbody.append(row);
      }
      table.append(tbody); specs.append(table);
    } else if (product.technicalSpecifications) specs.append(this.el('p', 'rm-reference-source-text', product.technicalSpecifications));
    else specs.append(this.el('p', 'rm-reference-muted', this.t('no_specs')));
    details.append(specs);
    if (product.sourceApplicationNotes) {
      const notes = this.el('div', 'rm-reference-detail-group');
      notes.append(this.el('h3', '', this.t('notes')), this.el('p', 'rm-reference-source-text', product.sourceApplicationNotes));
      details.append(notes);
    }
    if (product.variants?.length > 1) {
      const group = this.el('div', 'rm-reference-detail-group');
      group.append(this.el('h3', '', this.t('variants')));
      const list = this.el('ul', 'rm-reference-note-list');
      for (const variant of product.variants) {
        const values = [variant.sku, ...(variant.optionValues ?? []).filter((option) => option.name !== 'Default Title').map((option) => `${option.optionName}: ${option.name}`)];
        if (variant.barcode) values.push(`${this.t('ean')}: ${variant.barcode}`);
        const price = retailPriceSummary({ variants: [variant], currency: product.currency });
        if (price) {
          const format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: price.currency });
          values.push(`${this.t('retail_price')}: ${format.format(price.min)} ${price.currency}`);
          values.push(`${format.format(ukVatComparison(price.min).gross)} ${price.currency} ${this.t('uk_vat_inclusive')}`);
        } else values.push(this.t('retail_pending'));
        list.append(this.el('li', '', values.join(' · ')));
      }
      group.append(list); details.append(group);
    }
    for (const [field, label] of [['fittingNotes', 'fitting_notes'], ['includedFittings', 'included_fittings'], ['additionalProductNotes', 'additional_notes']]) {
      const notes = product[field];
      if (!notes?.length) continue;
      const group = this.el('div', 'rm-reference-detail-group');
      group.append(this.el('h3', '', this.t(label)));
      const list = this.el('ul', 'rm-reference-note-list');
      for (const text of Array.isArray(notes) ? notes : [notes]) list.append(this.el('li', '', text));
      group.append(list); details.append(group);
    }
    article.append(details);
    const actions = this.el('div', 'rm-reference-card-actions');
    const application = this.el('a', 'rm-reference-application-link', this.t('applications'));
    application.href = `/pages/application-lookup?part=${encodeURIComponent(product.sku || product.partNumber)}`;
    actions.append(application);
    if (canOpenProduct) {
      const productLink = this.el('a', 'rm-button', this.t('product'));
      productLink.href = `/products/${encodeURIComponent(product.handle)}`;
      actions.append(productLink);
    } else if (this.dataset.approvedTrade !== 'true') {
      const login = this.el('a', 'rm-button rm-button--light', this.t('login'));
      login.href = this.dataset.loginUrl;
      actions.append(login);
    }
    article.append(this.el('p', 'rm-reference-muted rm-reference-pending', this.t('availability')), actions);
    return article;
  }

  async renderVehicles(query, version) {
    this.vehicleStatus.textContent = this.t('vehicle_loading');
    try {
      const { module, applications, index } = await this.loadApplications();
      if (version !== this.searchVersion) return;
      const referenceMatches = module.filterApplications(index, { part: query });
      const textMatches = findVehicleTextMatches(applications, query);
      const matches = [...new Map([...referenceMatches, ...textMatches].map((row) => [row.rowId, row])).values()];
      if (!matches.length) { this.vehicleStatus.textContent = this.t('vehicle_none'); return; }
      const shown = matches.slice(0, 10);
      const list = this.el('ul', 'rm-reference-vehicle-list');
      for (const row of shown) {
        const li = this.el('li');
        const link = this.el('a');
        const params = new URLSearchParams({ make: row.make, model: row.model, year: row.yearLabel });
        if (referenceMatches.includes(row)) params.set('part', query);
        link.href = `/pages/application-lookup?${params.toString()}`;
        link.append(this.el('strong', '', [row.make, row.model].filter(Boolean).join(' · ')), this.el('span', '', `${this.t('year')}: ${row.yearLabel || this.t('unspecified')}`));
        li.append(link); list.append(li);
      }
      this.vehicleResults.replaceChildren(list);
      this.vehicleStatus.textContent = this.t('vehicle_count', { total: matches.length.toLocaleString('en-GB'), shown: shown.length });
    } catch {
      if (version === this.searchVersion) this.vehicleStatus.textContent = this.t('vehicle_error');
    }
  }
}

if (globalThis.customElements && !customElements.get('remax-reference-finder')) customElements.define('remax-reference-finder', RemaxReferenceFinder);
